import { CustomPage } from '@/types/pages';

import sanityClient from '@/lib/sanity';
import sanityImageUrlBuilder from '@sanity/image-url';

export const imageUrlBuilder = sanityImageUrlBuilder(sanityClient);

/**
 * Social preview image for a Sanity-driven page: the background image of its first
 * `header` block, which is the page's main header (FOX2-197). Crawlers need their own
 * crop — the header is served full-bleed for the page, which LinkedIn and X render as a
 * small thumbnail. Returns undefined when the page ships no header image, so MainLayout's
 * branded fallback takes over.
 *
 * Lives here rather than in `helpers.tsx` so the test suite can import it: helpers pulls in
 * a JSX `.js` component and a CSS module, neither of which the `tsx --test` runner can
 * transform.
 */
export const getHeaderOgImageUrl = (
  page: CustomPage | undefined,
  width: number,
  height: number,
): string | undefined => {
  const header = (page?.content || []).find((block) => block._type === 'header') as
    | { bgImage?: { hardCoded?: string; asset?: unknown } | string }
    | undefined;
  const bgImage = header?.bgImage;
  if (!bgImage) return undefined;
  if (typeof bgImage === 'string') return bgImage;
  if (bgImage.hardCoded) return bgImage.hardCoded;
  if (!bgImage.asset) return undefined;
  // Pass the whole image object, not just the asset: the hotspot an editor set lives on the
  // wrapper, and this is the one place that reframes a wide header down to a card. Cropping
  // from the asset alone centres the crop and can cut the subject out of the share image.
  return imageUrlBuilder.image(bgImage).width(width).height(height).fit('crop').auto('format').quality(80).url();
};
